import React, { useState } from 'react';
import { 
  ScatterChart, 
  Scatter, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Legend 
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { cropYieldData, weatherData, provinces, crops } from '../data/mockData';

const CorrelationChart = () => {
  const [selectedCrop, setSelectedCrop] = useState('Wheat');
  const [selectedWeatherMetric, setSelectedWeatherMetric] = useState('avgTemp');
  
  const prepareCorrelationData = () => {
    const data = [];
    
    provinces.forEach(province => {
      const cropData = cropYieldData[selectedCrop][province.id];
      const weatherTempData = weatherData[province.id].temperature;
      const weatherPrecipData = weatherData[province.id].precipitation;
      
      cropData.forEach((crop, index) => {
        const weather = weatherTempData[index];
        const precip = weatherPrecipData[index];
        
        let weatherValue;
        switch(selectedWeatherMetric) {
          case 'avgTemp': weatherValue = weather.avg; break;
          case 'minTemp': weatherValue = weather.min; break;
          case 'maxTemp': weatherValue = weather.max; break;
          case 'totalPrecip': weatherValue = precip.total; break;
          case 'growingPrecip': weatherValue = precip.growing; break;
          default: weatherValue = weather.avg;
        }
        
        data.push({
          province: province.name,
          provinceId: province.id,
          year: crop.year,
          yield: crop.yield,
          weather: weatherValue,
          production: crop.production
        });
      });
    });
    
    return data;
  };
  
  const calculateCorrelation = (data) => {
    const n = data.length;
    const sumX = data.reduce((sum, d) => sum + d.weather, 0);
    const sumY = data.reduce((sum, d) => sum + d.yield, 0);
    const sumXY = data.reduce((sum, d) => sum + d.weather * d.yield, 0);
    const sumX2 = data.reduce((sum, d) => sum + d.weather * d.weather, 0);
    const sumY2 = data.reduce((sum, d) => sum + d.yield * d.yield, 0);
    
    const correlation = (n * sumXY - sumX * sumY) / 
      Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
    
    return correlation;
  };
  
  const data = prepareCorrelationData();
  const correlation = calculateCorrelation(data);
  
  const getCorrelationStrength = (corr) => {
    const abs = Math.abs(corr);
    if (abs > 0.7) return { strength: 'Strong', color: 'bg-green-100 text-green-800' };
    if (abs > 0.3) return { strength: 'Moderate', color: 'bg-yellow-100 text-yellow-800' };
    return { strength: 'Weak', color: 'bg-red-100 text-red-800' };
  };
  
  const getWeatherLabel = () => {
    switch(selectedWeatherMetric) {
      case 'avgTemp': return 'Average Temperature (°C)';
      case 'minTemp': return 'Minimum Temperature (°C)';
      case 'maxTemp': return 'Maximum Temperature (°C)';
      case 'totalPrecip': return 'Total Precipitation (mm)';
      case 'growingPrecip': return 'Growing Season Precipitation (mm)';
      default: return 'Weather Metric';
    }
  };
  
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border rounded shadow">
          <p className="font-medium">{`${data.province} (${data.year})`}</p>
          <p style={{ color: payload[0].color }}>
            {`Yield: ${data.yield.toFixed(1)} t/ha`}
          </p>
          <p style={{ color: payload[0].color }}>
            {`${getWeatherLabel()}: ${data.weather.toFixed(1)}`}
          </p>
        </div>
      );
    }
    return null;
  };
  
  const provinceColors = {
    'Ontario': '#8884d8',
    'Saskatchewan': '#82ca9d',
    'Alberta': '#ffc658',
    'Manitoba': '#ff7300'
  };
  
  const strengthData = getCorrelationStrength(correlation);
  
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Weather vs Yield Correlation</span>
          <div className="flex gap-2">
            <Select value={selectedCrop} onValueChange={setSelectedCrop}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {crops.map(crop => (
                  <SelectItem key={crop} value={crop}>
                    {crop}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedWeatherMetric} onValueChange={setSelectedWeatherMetric}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="avgTemp">Average Temperature</SelectItem>
                <SelectItem value="minTemp">Minimum Temperature</SelectItem>
                <SelectItem value="maxTemp">Maximum Temperature</SelectItem>
                <SelectItem value="totalPrecip">Total Precipitation</SelectItem>
                <SelectItem value="growingPrecip">Growing Season Precip</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex items-center gap-4">
          <div className="text-sm text-gray-600">
            Correlation Coefficient: <span className="font-medium">{correlation.toFixed(3)}</span>
          </div>
          <Badge className={strengthData.color}>
            {strengthData.strength} Correlation
          </Badge>
        </div>
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="weather" 
                name={getWeatherLabel()}
                label={{ value: getWeatherLabel(), position: 'insideBottom', offset: -10 }}
              />
              <YAxis 
                dataKey="yield" 
                name="Yield (t/ha)"
                label={{ value: 'Yield (t/ha)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {provinces.map(province => (
                <Scatter
                  key={province.id}
                  data={data.filter(d => d.province === province.name)}
                  fill={provinceColors[province.name]}
                  name={province.name}
                />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        
        <div className="mt-4 text-xs text-gray-500">
          <p>Each point represents a year's data for a specific province.</p>
          <p>Correlation ranges from -1 (perfect negative) to +1 (perfect positive).</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default CorrelationChart;